// 레이드 효과음 엔진: 레시피 은행(레이드 판에서만 채우고, 나가면 비운다) + 미리 만든 목소리 그래프 + 묶음 상한·우선순위 + 반복 채널.
// 그래프: 목소리 i = [소스] → Gain → StereoPanner → (dry → 레이드 버스) / (duck send → 덕킹 Gain → 레이드 버스), 레이드 버스 → 효과음 버스.
// 반복 채널(날갯짓 1 + 불 장판 지글 3): Gain → StereoPanner → 레이드 버스. 소스는 시작·멈춤 경계에서만 만든다.
// 재생 경로는 렌더하지 않고 JS 객체도 만들지 않는다(일회용 AudioBufferSourceNode 하나만 — c019 엔진과 같은 예외).
// 워밍은 레이드 판일 때만: 유휴 콜백·프레임 예산(4ms) 안에서 렌더 단계를 잇는다. 순서는 경고·보스·판정 → 직업 AI → 반복음·징글.
import type { GameEvent } from '../../shared/events';
import { BossState, Scene, type GameView } from '../../shared/types';
import { WyvernPattern } from '../../shared/content/raid';
import { distanceDb, panFor } from '../decide';
import { dbToGain } from '../duck';
import { RecipeJob } from '../synth/dsp';
import { SynthVoicePool } from '../synth/pool';
import { brandTickAt, brandTickRate, breathFireCrossed, decideRaid, newRaidDecideState, RaidCueList, type RaidCue } from './decide';
import { makeLoop, RAID_CATS, RAID_DEFS, RAID_NAMES, RAID_SR, RCat, raidSeed, RY } from './recipes';

/** 레이드 목소리 수(반복 채널 별도) */
export const RAID_VOICES = 10;
/** 불 장판 지글 채널 수(가까운 장판부터) */
export const FIRE_CHANNELS = 3;
/** 재생마다 흔드는 폭: 피치(센트)·음량(dB). 징글(steady)은 흔들지 않는다 */
export const RAID_JITTER = { cents: 18, db: 0.6 } as const;
/** 큰 타격 덕킹(party 묶음만) */
export const RAID_DUCK = { db: -6, attack: 0.008, hold: 0.09, release: 0.12 } as const;
/** 레이드 판을 떠나고 이만큼(초) 지나면 은행을 비운다(다른 판의 메모리를 쓰지 않는다) */
export const RELEASE_AFTER = 8;
/** 워밍 예산(ms)·건너뛸 무거운 프레임(초) — c019와 같은 값 */
const WARM_BUDGET_MS = 4;
const WARM_SKIP_DT = 0.02;
/** 무거운 프레임만 이어질 때: 이만큼(ms) 못 했으면 그 프레임에 2ms만 쓴다(느린 폰에서도 은행이 끝내 찬다) */
const WARM_STARVE_MS = 250;
const WARM_STARVE_BUDGET_MS = 2;
/** 반복 채널 음량 갱신 문턱(선형 비율)과 시간상수(초) */
const LOOP_EPS = 0.06;
const LOOP_TC = 0.08;
/** 불 장판 지글: 사라지기 전 페이드(틱) */
const FIRE_FADE_TICKS = 40;
/** 경고·보스 소리는 거리 감쇠를 절반만(멀리서도 들려야 한다) */
const FAR_SCALE = 0.5;
const PENDING = 12;

/** 칸(은행 번호) → 소리 번호·변형 번호. 소리마다 variants개 칸을 차지한다 */
const SLOT_ID: number[] = [];
const SLOT_V: number[] = [];
const FIRST_SLOT: number[] = [];
RAID_DEFS.forEach((d, id) => { FIRST_SLOT.push(SLOT_ID.length); for (let v = 0; v < d.variants; v++) { SLOT_ID.push(id); SLOT_V.push(v); } });
export const RAID_BANK_SLOTS = SLOT_ID.length;
/** 워밍 순서: 경고·보스·판정 → 직업 AI(행동·명중) → 반복음·징글. 모든 묶음이 한 번씩 들어간다(시험) */
const URGENT = (c: number): boolean => c === RCat.Tele || c === RCat.Boss || c === RCat.Strike;
const WARM_ORDER: number[] = [];
const PARTY = (c: number): boolean => c === RCat.Party || c === RCat.PartyHit;
for (const pick of [URGENT, PARTY, (c: number) => c === RCat.Loop || c === RCat.Jingle]) {
  for (let k = 0; k < RAID_BANK_SLOTS; k++) if (pick(RAID_DEFS[SLOT_ID[k]].cat)) WARM_ORDER.push(k);
}

export interface RaidAudioStats {
  active: boolean; bankFilled: number; bankTotal: number; released: number; rendered: number; lateRenders: number; missed: number;
  played: number; stolen: number; droppedSpacing: number; droppedCap: number; droppedFull: number; gated: number; ducks: number;
  breaths: number; brandTicks: number; byName: Record<string, number>;
  loops: { wing: boolean; fires: number; wingStarts: number; fireStarts: number };
}

/**
 * 렌더 작업 하나와 그 작업만 쓰는 출력 배열(Codex P2 #216: 급한 렌더가 공용 배열을 비워 멈춰 둔 워밍 작업의 샘플을 덮어썼다).
 * 엔진은 워밍용·급한 렌더용 두 개를 생성 때 만들고, 서로의 배열을 쓰지 않는다. commit이 결과를 AudioBuffer로 복사하므로 다음 작업이 배열을 다시 써도 된다.
 */
class RenderLane {
  readonly job = new RecipeJob();
  /** 지금 렌더 중인 은행 칸(-1 = 쉼) */
  slot = -1;
  constructor(readonly out: Float32Array<ArrayBuffer>) {}
  begin(k: number): void {
    const id = SLOT_ID[k], def = RAID_DEFS[id];
    this.job.begin(def.recipe, RAID_SR, raidSeed(id, SLOT_V[k]), RAID_CATS[def.cat].targetDb, this.out);
    this.slot = k;
  }
}

class LoopVoice {
  src: AudioBufferSourceNode | null = null;
  readonly gain: GainNode;
  readonly pan: StereoPannerNode;
  level = 0;
  starts = 0;
  constructor(private readonly ctx: BaseAudioContext, dest: AudioNode) {
    this.gain = ctx.createGain(); this.pan = ctx.createStereoPanner();
    this.gain.gain.value = 0;
    this.gain.connect(this.pan); this.pan.connect(dest);
  }
  /** amount(선형, 0 = 멈춤)로 맞춘다. 바뀐 폭이 작으면 자동화 이벤트를 만들지 않는다 */
  set(buf: AudioBuffer | null, amount: number, pan: number): void {
    if (!buf || amount <= 0.001) { this.stop(); return; }
    const t = this.ctx.currentTime;
    if (!this.src) {
      const s = this.ctx.createBufferSource();
      s.buffer = buf; s.loop = true; s.connect(this.gain);
      // 시작 위치를 흩어 여러 채널이 같은 위상으로 겹치지 않게
      s.start(t, (this.starts * 0.37) % Math.max(0.01, buf.duration));
      this.src = s; this.starts++;
      this.gain.gain.cancelScheduledValues(t); this.gain.gain.setValueAtTime(0, t);
      this.level = 0;
    }
    if (Math.abs(amount - this.level) > LOOP_EPS * Math.max(this.level, 0.05)) {
      this.gain.gain.setTargetAtTime(amount, t, LOOP_TC);
      this.level = amount;
    }
    if (Math.abs(this.pan.pan.value - pan) > 0.05) this.pan.pan.setTargetAtTime(pan, t, LOOP_TC);
  }
  stop(): void {
    if (!this.src) return;
    try { this.src.stop(); } catch { /* 이미 멈춤 */ }
    this.src.disconnect(); this.src = null; this.level = 0;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime); this.gain.gain.value = 0;
  }
  get on(): boolean { return this.src !== null; }
}

export class RaidAudioEngine {
  readonly bus: GainNode;
  readonly duckGain: GainNode;
  readonly pool: SynthVoicePool;
  private readonly vGain: GainNode[] = [];
  private readonly vPan: StereoPannerNode[] = [];
  private readonly vDry: GainNode[] = [];
  private readonly vWet: GainNode[] = [];
  private readonly vSrc: (AudioBufferSourceNode | null)[] = [];
  private readonly bank: (AudioBuffer | null)[] = new Array<AudioBuffer | null>(RAID_BANK_SLOTS).fill(null);
  /** 워밍 작업(예산 안에서 조금씩)과 급한 렌더(그 자리에서 끝까지) — 각자 출력 배열을 가진다 */
  private readonly warm: RenderLane;
  private readonly urgent: RenderLane;
  private orderAt = 0;
  private filled = 0;
  private readonly lastVariant = new Int8Array(RAID_DEFS.length).fill(-1);
  private readonly counts = new Uint32Array(RAID_DEFS.length);
  private readonly cues = new RaidCueList(4);
  private readonly pending = new RaidCueList(PENDING);
  private readonly st = newRaidDecideState();
  private readonly wing: LoopVoice;
  private readonly fires: LoopVoice[] = [];
  /** 가까운 불 장판 번호(재사용 배열) */
  private readonly near = new Int16Array(FIRE_CHANNELS);
  private readonly nearD = new Float64Array(FIRE_CHANNELS);
  private seed: number;
  private active = false;
  private inactiveFor = 0;
  private lastBreathTick = -1;
  /** 잿불 낙인 남은 틱(지난 프레임, -1 = 낙인 없음) — 카운트다운 틱 */
  private lastBrandLeft = -1;
  private brandTicks = 0;
  private lastWarmAt = -Infinity;
  private released = 0; private rendered = 0; private lateRenders = 0; private missed = 0; private gated = 0; private ducks = 0; private breaths = 0;

  constructor(private readonly ctx: BaseAudioContext, dest: AudioNode, private readonly gate: () => boolean,
    seed = 0x3c6ef372, private readonly now: () => number = () => performance.now()) {
    this.seed = seed | 0 || 1;
    this.bus = ctx.createGain();
    this.duckGain = ctx.createGain();
    this.duckGain.connect(this.bus);
    this.bus.connect(dest);
    for (let i = 0; i < RAID_VOICES; i++) {
      const g = ctx.createGain(), p = ctx.createStereoPanner(), dry = ctx.createGain(), wet = ctx.createGain();
      g.connect(p); p.connect(dry); p.connect(wet); dry.connect(this.bus); wet.connect(this.duckGain);
      wet.gain.value = 0;
      this.vGain.push(g); this.vPan.push(p); this.vDry.push(dry); this.vWet.push(wet); this.vSrc.push(null);
    }
    this.pool = new SynthVoicePool(RAID_VOICES, RAID_CATS);
    this.wing = new LoopVoice(ctx, this.bus);
    for (let i = 0; i < FIRE_CHANNELS; i++) this.fires.push(new LoopVoice(ctx, this.bus));
    let maxDur = 0;
    for (const d of RAID_DEFS) maxDur = Math.max(maxDur, d.recipe.dur);
    const len = Math.ceil(maxDur * RAID_SR) + 1;
    this.warm = new RenderLane(new Float32Array(len));
    this.urgent = new RenderLane(new Float32Array(len));
  }

  get ready(): boolean { return this.filled >= RAID_BANK_SLOTS; }
  get bankFilled(): number { return this.filled; }

  /** 이벤트 하나(레이드 판이 아니어도 부른다 — 레이드 이벤트가 아니면 아무것도 하지 않는다) */
  onEvent(e: GameEvent, raidActive: boolean): void {
    decideRaid(e, this.cues, this.st, raidActive);
    for (let i = 0; i < this.cues.count; i++) this.enqueue(this.cues.items[i]);
    this.cues.clear();
  }

  private enqueue(c: RaidCue): void {
    const P = this.pending, prio = RAID_DEFS[c.id].prio;
    let slot = P.add(c.id, c.gainDb, c.rate, c.x, c.z, c.delay);
    if (!slot) {
      let lo = -1;
      for (let i = 0; i < P.count; i++) if (lo < 0 || RAID_DEFS[P.items[i].id].prio <= RAID_DEFS[P.items[lo].id].prio) lo = i;
      if (lo < 0 || RAID_DEFS[P.items[lo].id].prio >= prio) return;
      slot = P.items[lo];
      slot.id = c.id; slot.gainDb = c.gainDb; slot.rate = c.rate; slot.x = c.x; slot.z = c.z; slot.delay = c.delay;
    }
    slot.positional = c.positional;
  }

  /**
   * 렌더 프레임마다(이벤트를 다 읽은 뒤): 레이드 판 여부·은행 워밍·브레스 불길 틱·반복 채널·모인 소리 내기.
   * heroX·heroZ = 듣는 자리(영웅).
   */
  update(view: GameView, frameDt: number): void {
    const raid = view.raid, on = !!raid && raid.active;
    if (on) { this.active = true; this.inactiveFor = 0; }
    else if (this.active || this.filled > 0) {
      this.inactiveFor += frameDt > 0 && frameDt < 0.25 ? frameDt : 0;
      if (this.active) { this.active = false; this.stopLoops(); this.lastBreathTick = -1; this.lastBrandLeft = -1; this.st.lastPattern = -1; }
      if (this.inactiveFor > RELEASE_AFTER && this.filled > 0) this.releaseBank();
    }
    if (this.active) this.warmStep(frameDt);
    const hx = view.hero.x, hz = view.hero.z;
    if (on && raid && view.flow.scene === Scene.Dungeon) {
      // 브레스 불길: 렌더가 불꽃을 뿜는 틱(비행 2단계·패턴 breath·Cast 상태)
      const b = view.boss;
      if (raid.air > 0 && raid.pattern === WyvernPattern.breath && b.state === BossState.Cast) {
        if (this.lastBreathTick >= 0 && breathFireCrossed(this.lastBreathTick, b.stateTick)) {
          const c = this.pending.add(RY.wyvBreath, 0, 1, b.x, b.z);
          if (c) this.breaths++;
        }
        this.lastBreathTick = b.stateTick;
      } else this.lastBreathTick = -1;
      this.brandCountdown(view);
    } else this.lastBrandLeft = -1;
    this.flush(hx, hz);
    this.updateLoops(view, on, hx, hz);
  }

  /**
   * 하드 잿불 낙인 카운트다운(view.raid.brandSlots·brandLeft): 틱 자리를 지날 때마다 짧은 쇠 '틱'(마지막 1.5초는 빨라지고 높게).
   * 영웅에게 붙었으면 영웅 자리에서 +3dB(내 일이다), 아니면 낙인이 붙은 첫 용병 자리에서. 일시정지·히트스톱이면 남은 틱이 멈춰 틱도 멈춘다
   */
  private brandCountdown(view: GameView): void {
    const r = view.raid!, mask = (r.brandSlots ?? 0) | 0, left = r.brandLeft ?? 0;
    if (!mask || left <= 0) { this.lastBrandLeft = -1; return; }
    const k = this.lastBrandLeft >= 0 ? brandTickAt(this.lastBrandLeft, left) : -1;
    this.lastBrandLeft = left;
    if (k < 0) return;
    let x = view.hero.x, z = view.hero.z, db = 3;
    if (!(mask & 1)) {
      db = 0;
      for (let s = 1; s < 4; s++) if (mask & (1 << s)) { const m = r.mercs[s - 1]; if (m) { x = m.x; z = m.z; } break; }
    }
    if (this.pending.add(RY.wyvBrandTick, db, brandTickRate(k), x, z)) this.brandTicks++;
  }

  /**
   * 모인 소리를 우선순위 높은 것부터, 같으면 시작 예약이 이른 것부터 낸다(삽입 정렬, 할당 없음).
   * 게임은 직업 AI 명중(Hit)을 발사(RaidMerc)보다 먼저 내므로, 예약 순으로 줄 세워야 발사음이 먼저 자리를 잡는다(Codex P2 #216)
   */
  private flush(hx: number, hz: number): void {
    const P = this.pending, it = P.items;
    for (let i = 1; i < P.count; i++) {
      const c = it[i], p = RAID_DEFS[c.id].prio;
      let j = i - 1;
      while (j >= 0 && (RAID_DEFS[it[j].id].prio < p || (RAID_DEFS[it[j].id].prio === p && it[j].delay > c.delay))) { it[j + 1] = it[j]; j--; }
      it[j + 1] = c;
    }
    for (let i = 0; i < P.count; i++) this.play(it[i], hx, hz);
    P.clear();
  }

  private rand(): number {
    let s = this.seed;
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    this.seed = s;
    return (s >>> 0) / 4294967296;
  }

  /** 소리 하나. 실제로 시작(예약)했으면 true */
  play(c: RaidCue, hx: number, hz: number): boolean {
    if (!this.gate()) { this.gated++; return false; }
    const def = RAID_DEFS[c.id], first = FIRST_SLOT[c.id], n = def.variants;
    let v = n > 1 ? Math.min(n - 1, (this.rand() * n) | 0) : 0;
    if (n > 1 && v === this.lastVariant[c.id]) v = (v + 1) % n;
    let buf = this.bank[first + v];
    if (!buf) for (let o = 0; o < n && !buf; o++) { const b = this.bank[first + o]; if (b) { buf = b; v = o; } }
    if (!buf) {
      // 경고·보스·판정·징글은 빠지면 안 된다: 그 자리에서 렌더. 직업 AI 소리는 건너뛰고 워밍이 곧 채운다
      if (PARTY(def.cat)) { this.missed++; return false; }
      buf = this.renderNow(first + v);
    }
    let rate = c.rate;
    if (!def.steady) {
      const x = (RAID_JITTER.cents / 1200) * Math.LN2 * (this.rand() * 2 - 1);
      rate *= 1 + x + 0.5 * x * x;
    }
    const t = this.ctx.currentTime;
    const vi = this.pool.acquire(def.cat, def.prio, t + Math.max(0, c.delay), buf.duration / rate);
    if (vi < 0) return false;
    this.lastVariant[c.id] = v;
    const at = this.pool.startAt;
    const old = this.vSrc[vi];
    if (old) { try { old.stop(at); } catch { /* 이미 멈춤 */ } }
    let db = def.gainDb + c.gainDb + (def.steady ? 0 : RAID_JITTER.db * (this.rand() * 2 - 1));
    let pan = 0;
    if (c.positional) {
      const dx = c.x - hx, dz = c.z - hz;
      pan = panFor(dx);
      const far = def.cat === RCat.Boss || def.cat === RCat.Tele ? FAR_SCALE : 1;
      db += distanceDb(Math.sqrt(dx * dx + dz * dz)) * far;
    }
    const duck = RAID_CATS[def.cat].duck;
    const g = this.vGain[vi].gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(dbToGain(db), at);
    this.vPan[vi].pan.setValueAtTime(pan, at);
    this.vDry[vi].gain.setValueAtTime(duck ? 0 : 1, at);
    this.vWet[vi].gain.setValueAtTime(duck ? 1 : 0, at);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    src.connect(this.vGain[vi]);
    src.start(at);
    this.vSrc[vi] = src;
    this.counts[c.id]++;
    return true;
  }

  /** 큰 타격 덕킹(직업 AI 묶음만) */
  duck(): void {
    if (!this.gate() || !this.active) return;
    const t = this.ctx.currentTime, g = this.duckGain.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.setTargetAtTime(dbToGain(RAID_DUCK.db), t, RAID_DUCK.attack);
    g.setTargetAtTime(1, t + RAID_DUCK.hold, RAID_DUCK.release);
    this.ducks++;
  }

  // ── 반복 채널 ────────────────────────────────────
  private updateLoops(view: GameView, on: boolean, hx: number, hz: number): void {
    const raid = view.raid;
    // 전투 장면에서만: 전멸·결과 화면에서는 레이드 상태(공중·불 장판)가 멈춘 채 남아 있어도 반복음을 끈다(Codex P2 #216). 징글은 단발음이라 그대로 난다
    if (!on || !raid || !this.gate() || view.flow.paused || view.flow.scene !== Scene.Dungeon) { this.stopLoops(); return; }
    // 날갯짓: 비룡이 공중에 있는 동안(이륙 시작 ~ 착지). 높이가 낮을수록(이륙·착지 끝) 조금 작게
    const b = view.boss;
    if (raid.air > 0 && b.state !== BossState.Dying && b.state !== BossState.Dead) {
      const def = RAID_DEFS[RY.wingLoop], dx = b.x - hx, dz = b.z - hz;
      const db = def.gainDb + distanceDb(Math.sqrt(dx * dx + dz * dz)) * FAR_SCALE;
      this.wing.set(this.bank[FIRST_SLOT[RY.wingLoop]], dbToGain(db) * (0.6 + 0.4 * Math.min(1, raid.air * 3)), panFor(dx));
    } else this.wing.stop();
    // 불 장판: 가까운 FIRE_CHANNELS개(채널은 소리가 같아 대상이 바뀌어도 다시 시작하지 않고 음량·팬만 옮긴다)
    let k = 0;
    this.nearD.fill(Infinity); this.near.fill(-1);
    const fires = raid.fires;
    for (let i = 0; i < fires.length; i++) {
      const f = fires[i];
      if (!f.active) continue;
      const d = Math.hypot(f.x - hx, f.z - hz);
      for (let j = 0; j < FIRE_CHANNELS; j++) {
        if (d < this.nearD[j]) {
          for (let m = FIRE_CHANNELS - 1; m > j; m--) { this.nearD[m] = this.nearD[m - 1]; this.near[m] = this.near[m - 1]; }
          this.nearD[j] = d; this.near[j] = i; break;
        }
      }
      k++;
    }
    const fbuf = this.bank[FIRST_SLOT[RY.fireLoop]], fdef = RAID_DEFS[RY.fireLoop];
    for (let j = 0; j < FIRE_CHANNELS; j++) {
      const i = this.near[j];
      if (i < 0) { this.fires[j].stop(); continue; }
      const f = fires[i];
      // 장판 안이면 감쇠 없음(반경만큼 가깝게 친다), 사라지기 직전 페이드
      const d = Math.max(0, this.nearD[j] - f.radius);
      const fade = Math.min(1, f.ttl / FIRE_FADE_TICKS);
      this.fires[j].set(fbuf, dbToGain(fdef.gainDb + distanceDb(d)) * fade, panFor(f.x - hx));
    }
    void k;
  }

  private stopLoops(): void {
    this.wing.stop();
    for (const f of this.fires) f.stop();
  }

  // ── 은행 워밍 ────────────────────────────────────
  private warmStep(frameDt: number): void {
    if (this.ready) return;
    if (frameDt > WARM_SKIP_DT) {
      if (this.now() - this.lastWarmAt >= WARM_STARVE_MS) this.warmFor(WARM_STARVE_BUDGET_MS);
      return;
    }
    this.warmFor(WARM_BUDGET_MS);
  }

  /** 유휴 콜백에서도 부른다(index.ts). 레이드 판이 아니면 아무것도 하지 않는다 */
  warmFor(budgetMs: number): void {
    if (!this.active || this.ready) return;
    const t0 = this.now();
    this.lastWarmAt = t0;
    const W = this.warm;
    for (;;) {
      if (W.slot < 0) {
        const k = this.nextSlot();
        if (k < 0) return;
        W.begin(k);
      }
      if (W.job.step()) { this.commit(W); }
      if (this.now() - t0 >= budgetMs) return;
    }
  }

  private nextSlot(): number {
    while (this.orderAt < WARM_ORDER.length) {
      const k = WARM_ORDER[this.orderAt];
      if (!this.bank[k] && k !== this.warm.slot) return k;
      this.orderAt++;
    }
    return -1;
  }

  /** 작업 결과를 은행에 넣고 그 작업을 쉬게 한다(결과는 AudioBuffer로 복사되어 작업 배열과 떨어진다) */
  private commit(lane: RenderLane): AudioBuffer {
    const k = lane.slot, def = RAID_DEFS[SLOT_ID[k]];
    lane.slot = -1;
    const out = def.loop ? makeLoop(lane.job.output, RAID_SR, def.loop) : lane.job.output;
    const buf = this.ctx.createBuffer(1, out.length, RAID_SR);
    buf.copyToChannel(out as Float32Array<ArrayBuffer>, 0);
    this.bank[k] = buf;
    this.filled++;
    this.rendered++;
    return buf;
  }

  /**
   * 은행에 없는 급한 소리: 워밍이 바로 그 칸을 렌더 중이면 그 작업을 끝까지, 아니면 급한 렌더 작업(자기 배열)으로 그 자리에서 렌더한다.
   * 멈춰 둔 워밍 작업은 건드리지 않으므로 다음 워밍에서 그대로 이어지고 샘플도 끊김 없이 렌더한 것과 같다(시험)
   */
  private renderNow(k: number): AudioBuffer {
    this.lateRenders++;
    const lane = this.warm.slot === k ? this.warm : this.urgent;
    if (lane === this.urgent) lane.begin(k);
    while (!lane.job.step()) { /* 끝까지 */ }
    return this.commit(lane);
  }

  private releaseBank(): void {
    this.bank.fill(null);
    this.filled = 0; this.orderAt = 0; this.warm.slot = -1; this.urgent.slot = -1;
    this.released++;
  }

  /** 모두 멈춘다(탭 가림·음소거). 은행은 남긴다 */
  silence(): void {
    for (let i = 0; i < this.vSrc.length; i++) {
      const s = this.vSrc[i];
      if (s) { try { s.stop(); } catch { /* 이미 멈춤 */ } this.vSrc[i] = null; }
    }
    this.pool.clear();
    this.stopLoops();
  }

  stats(): RaidAudioStats {
    const byName: Record<string, number> = {};
    RAID_NAMES.forEach((n, i) => { if (this.counts[i] > 0) byName[n] = this.counts[i]; });
    const p = this.pool;
    let fires = 0, fireStarts = 0;
    for (const f of this.fires) { if (f.on) fires++; fireStarts += f.starts; }
    return {
      active: this.active, bankFilled: this.filled, bankTotal: RAID_BANK_SLOTS, released: this.released, rendered: this.rendered,
      lateRenders: this.lateRenders, missed: this.missed, played: p.played, stolen: p.stolen, droppedSpacing: p.droppedSpacing,
      droppedCap: p.droppedCap, droppedFull: p.droppedFull, gated: this.gated, ducks: this.ducks, breaths: this.breaths, brandTicks: this.brandTicks, byName,
      loops: { wing: this.wing.on, fires, wingStarts: this.wing.starts, fireStarts },
    };
  }
}
